import React from "react";
import { Link } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import { formatPrice, resolveImg } from "../lib/api";
import { Plus } from "lucide-react";
import SmartImage from "./SmartImage";
import StockBadge from "./StockBadge";
import { stockOf } from "../lib/stock";

const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;

function isNewArrival(createdAt) {
  if (!createdAt) return false;
  const t = Date.parse(createdAt);
  if (Number.isNaN(t)) return false;
  return Date.now() - t < FOURTEEN_DAYS_MS;
}

export default function ProductCard({ product }) {
  const { addItem } = useCart();
  const isNew = isNewArrival(product.created_at);

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product, 1);
  };

  return (
    <Link
      to={`/product/${product.id}`}
      className="group block"
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative aspect-square overflow-hidden rounded-2xl bg-[var(--muted)] border border-[var(--border)]">
        <SmartImage
          src={resolveImg(product.image_url)}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        {isNew && (
          <div
            className="absolute top-0 left-4 pointer-events-none"
            data-testid={`new-ribbon-${product.id}`}
            aria-label="New arrival"
          >
            {/* Soft gold ribbon */}
            <div
              className="relative pt-2.5 pb-4 px-3 text-[10px] font-semibold tracking-[0.18em] uppercase text-[#5A3A12] shadow-md"
              style={{
                background: "linear-gradient(180deg, #F5D78A 0%, #E6BF60 100%)",
                clipPath: "polygon(0 0, 100% 0, 100% 100%, 50% 82%, 0 100%)",
              }}
            >
              New
            </div>
          </div>
        )}
        <button
          onClick={handleAdd}
          disabled={stockOf(product) <= 0}
          className="absolute bottom-3 right-3 bg-[var(--bg-surface)] text-[var(--ink-forest)] hover:bg-[var(--terracotta)] hover:text-white shadow-lg rounded-full p-2.5 opacity-0 group-hover:opacity-100 transition-all duration-300 disabled:hidden"
          aria-label={`Add ${product.name} to cart`}
          data-testid={`quick-add-${product.id}`}
        >
          <Plus className="w-4 h-4" />
        </button>
        <StockBadge stock={stockOf(product)} className="absolute bottom-3 left-3 shadow-sm" testid={`stock-badge-${product.id}`} />
      </div>
      <div className="pt-4">
        <p className="eyebrow text-[10px]">{categoryLabel(product.category)}</p>
        <h3 className="mt-1 font-serif text-lg text-[var(--ink-forest)] group-hover:text-[var(--terracotta)] transition-colors line-clamp-1">
          {product.name}
        </h3>
        <p className="mt-1 text-sm font-medium text-[var(--ink-forest)]">{formatPrice(product.price)}</p>
      </div>
    </Link>
  );
}

function categoryLabel(c) {
  if (c === "essential-oils") return "Essential Oil";
  if (c === "diy-supplies") return "DIY Supply";
  if (c === "garden-gifts") return "Home, Garden & Gift";
  return c;
}
